import { createAsyncThunk } from '@reduxjs/toolkit';
import { isValidCode } from '../../services/base.service';
import {
    getNeglectedOrDelinquentContact,
    getNeglectedOrDelinquentSchoolYears,
    postNeglectedOrDelinquentContact,
    putNeglectedOrDelinquentContact,
    getNeglectedOrDelinquentInformation,
    getNeglectedOrDelinquentInputOptionSets,
    postNeglectedOrDelinquentInformation,
    putNeglectedOrDelinquentInformation,
} from '../../services/neglectedOrDelinquent.service';
import { NeglectedOrDelinquentContactModel } from '../../types/NeglectedOrDelinquentContactModel';
import { NeglectedOrDelinquentInformationModel } from '../../types/NeglectedOrDelinquentInformationModel';
import { errorLoaded } from '../slices/errorSlice';

export const fetchNeglectedOrDelinquentContact = createAsyncThunk(
    'neglectedOrDelinquent/fetchContact',
    async (_, thunkAPI) => {
        const response = await getNeglectedOrDelinquentContact({
            signal: thunkAPI.signal,
        });
        if (response.status === 404) return null;
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return response.data || null;
    }
);

export const fetchNeglectedOrDelinquentSchoolYears = createAsyncThunk(
    'neglectedOrDelinquent/fetchSchoolYears',
    async (_, thunkAPI) => {
        const response = await getNeglectedOrDelinquentSchoolYears({
            signal: thunkAPI.signal,
        });
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return response.data;
    }
);

export const createNeglectedOrDelinquentContact = createAsyncThunk(
    'neglectedOrDelinquent/createContact',
    async (contact: NeglectedOrDelinquentContactModel, thunkAPI) => {
        const response = await postNeglectedOrDelinquentContact(contact, {
            signal: thunkAPI.signal,
        });
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return { ...contact, id: response.data };
    }
);

export const updateNeglectedOrDelinquentContact = createAsyncThunk(
    'neglectedOrDelinquent/updateContact',
    async (contact: NeglectedOrDelinquentContactModel, thunkAPI) => {
        const response = await putNeglectedOrDelinquentContact(contact, {
            signal: thunkAPI.signal,
        });
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return contact;
    }
);

export const fetchNeglectedOrDelinquentInformation = createAsyncThunk(
    'neglectedOrDelinquent/fetchInformation', async (schoolYear: string, thunkAPI) => {
        const response = await getNeglectedOrDelinquentInformation(
            schoolYear, { signal: thunkAPI.signal }
        );
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return response.data || null;
    }
);

export const fetchNeglectedOrDelinquentInputOptionSets = createAsyncThunk(
    'neglectedOrDelinquent/fetchInputOptionSets', async (_, thunkAPI) => {
        const response = await getNeglectedOrDelinquentInputOptionSets({
            signal: thunkAPI.signal,
        });
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return response.data;
    }
);

export const createNeglectedOrDelinquentInformation = createAsyncThunk(
    'neglectedOrDelinquent/createInformation',
    async (model: NeglectedOrDelinquentInformationModel, thunkAPI) => {
        const response = await postNeglectedOrDelinquentInformation(
            model, { signal: thunkAPI.signal }
        );
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return { ...model, id: response.data };
    }
);

export const updateNeglectedOrDelinquentInformation = createAsyncThunk(
    'neglectedOrDelinquent/updateInformation',
    async (model: NeglectedOrDelinquentInformationModel, thunkAPI) => {
        const response = await putNeglectedOrDelinquentInformation(
            model, { signal: thunkAPI.signal }
        );
        if (!isValidCode(response.status)) {
            if (!response.aborted) thunkAPI.dispatch(errorLoaded(response.error));
            return thunkAPI.rejectWithValue(response.error);
        }
        return model;
    }
);
